/**
 * A requester's own ticket — Step 71, shaped like the reference's
 * ticket_show: subject + ref + status, the request's answers (requester-
 * visible only — the server strips agent-only fields AND values), the public
 * conversation (private notes never reach this response), an "agent working
 * on your ticket" line, and a reply box.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Send, Paperclip } from 'lucide-react';
import { formatFieldValue, ticketRef } from '@itsm/shared';
import { portalApi } from '../../features/portal/api';
import { StatusChip } from '../../features/portal/StatusChip';
import { CatalogueIcon } from '../../features/fields/catalogueIcons';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { RichTextEditor } from '../../components/ui/richtext';
import { Skeleton } from '../../components/ui/skeleton';
import { fullTime } from '../../lib/time';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import { cn } from '../../lib/utils';

/** Sizes people read, not byte counts. Matches TicketAttachments so the two
 *  surfaces describe the same file the same way. */
function readable(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function PortalTicketPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.portalTicket(id),
    queryFn: () => portalApi.ticket(id),
    enabled: !!id,
  });
  const [reply, setReply] = useState('');
  const replyDirty = reply.replace(/<[^>]*>/g, '').trim().length > 0;
  useDirty(replyDirty);

  const send = useMutation({
    mutationFn: () => portalApi.reply(id, reply),
    onSuccess: (res) => {
      if (!res.ok) return;
      setReply('');
      void queryClient.invalidateQueries({ queryKey: qk.portalTicket(id) });
    },
  });

  if (isError)
    return (
      <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-4">
        <p className="text-sm font-medium text-destructive">Could not load this request</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );
  if (isPending || !data)
    return (
      <div className="mx-auto max-w-3xl space-y-3">
        <Skeleton className="h-8 w-80" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );

  const { ticket, catalogueItem, requestFields, values, messages, attachments } = data;
  const answered = requestFields.filter((f) => {
    if (f.definition.type === 'content') return false;
    const v = values[f.definition.key];
    return v !== undefined && v !== null && v !== '';
  });
  /**
   * A catalogue request's message zero is GENERATED from its answers, and
   * "What you asked for" above already renders those as labelled fields.
   * Showing both prints the whole form twice on one screen — the same
   * duplication Kai had removed from the agent side (2026-09-06), which the
   * portal was still doing.
   *
   * The generated body stays exactly as it is in the database and in the
   * confirmation email: the requester's copy needs the answers in it. It is
   * only wrong to show it beside the structured version.
   *
   * Everything else — an emailed-in request, a reported issue — keeps message
   * zero, because there it is the requester's own words.
   */
  const fromCatalogue = !!catalogueItem && answered.length > 0;
  const thread = fromCatalogue ? messages.slice(1) : messages;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Link
        to="/portal/requests"
        className="flex w-fit items-center gap-1.5 text-sm text-brand hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> My requests
      </Link>

      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold">{ticket.subject}</h1>
        <span className="text-sm text-muted-foreground">{ticketRef(ticket.type, ticket.number)}</span>
        <StatusChip status={ticket.status} category={ticket.statusCategory} />
      </div>
      <p className="text-xs text-muted-foreground">
        Raised {fullTime(ticket.createdAt)}
        {ticket.assigneeName ? ` · ${ticket.assigneeName} is working on your request` : ''}
      </p>

      {catalogueItem && answered.length > 0 && (
        <div className="rounded-lg border bg-card p-4">
          <div className="mb-3 flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              What you asked for
            </p>
            <Badge variant="outline" className="gap-1.5">
              <CatalogueIcon
                name={catalogueItem.icon}
                attachmentId={catalogueItem.iconAttachmentId}
                className="h-3 w-3"
              />
              {catalogueItem.name}
            </Badge>
          </div>
          {catalogueItem.description && (
            <p className="mb-3 whitespace-pre-line text-sm text-muted-foreground">
              {catalogueItem.description}
            </p>
          )}
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            {answered.map((f) => (
              <div key={f.definition.key} className="min-w-0">
                <dt className="text-xs text-muted-foreground">{f.definition.label}</dt>
                <dd className="break-words text-sm">
                  {formatFieldValue(f.definition, values[f.definition.key])}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/* Step 73 — the files on this request. The list is a convenience;
          /api/media/:id re-checks per owner when the bytes are fetched. */}
      {attachments.length > 0 && (
        <div className="rounded-lg border bg-card p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Attachments
          </p>
          <ul className="space-y-1.5">
            {attachments.map((f) => (
              <li key={f.id}>
                {f.migrated ? (
                  <a
                    href={`/api/media/${f.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-sm text-brand hover:underline"
                  >
                    <Paperclip className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{f.filename}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{readable(f.size)}</span>
                  </a>
                ) : (
                  // A known gap, never a broken link — the plan's rule for
                  // anything the migration could not retrieve.
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Paperclip className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{f.filename}</span>
                    <span className="shrink-0 text-xs">— not available</span>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        {thread.map((m) => (
          <div
            key={m.id}
            className={cn(
              'rounded-lg border p-4',
              m.kind === 'public_reply' ? 'bg-card' : 'border-brand/30 bg-brand/5',
            )}
          >
            <p className="mb-1.5 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">
                {m.mine ? 'You' : (m.authorName ?? 'Support')}
              </span>{' '}
              · {fullTime(m.createdAt)}
            </p>
            <div
              className="prose prose-sm dark:prose-invert max-w-none overflow-x-auto"
              dangerouslySetInnerHTML={{ __html: m.bodyHtml }}
            />
          </div>
        ))}
      </div>

      <div className="rounded-lg border bg-card p-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reply</p>
        <RichTextEditor value={reply} onChange={setReply} placeholder="Write your reply…" />
        <div className="mt-3 flex justify-end">
          <Button size="sm" disabled={!replyDirty || send.isPending} onClick={() => send.mutate()}>
            <Send /> Send
          </Button>
        </div>
      </div>
    </div>
  );
}
